import { Button, Center, Loader, Stack, Text } from "@mantine/core";
import { isMountayaApiError } from "@mountaya/client";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import {
  restRefusalMessage,
  useAcceptOAuthConsent,
  useOAuthConsent,
  useRejectOAuthConsent,
  useUserOrganizations,
} from "@mountayaapp/api";
import { useIdentityStore } from "@mountayaapp/theme";

import { AuthPageHeader } from "../auth/components/AuthPageHeader";
import { env } from "../env";
import { PaymentMethodModal } from "../iam/billing/components/PaymentMethodModal";
import { ConsentScreen } from "../oauth/components/ConsentScreen";

/**
 * restConfig is the REST client config the consent hooks consume — the session-auth
 * path against the internal API, so the browser is authenticated by its Ory cookie.
 * Hoisted to module scope so its identity is stable across renders (it feeds the
 * query/mutation keys).
 */
const restConfig = { baseUrl: env.apiBaseUrl };

/**
 * consentSearchSchema validates the /oauth2/consent search params (Zod-first). Ory
 * appends `consent_challenge` to the URL when it redirects the browser here after its
 * managed login; it is optional so the route renders a clear "missing challenge"
 * state rather than throwing when reached directly.
 */
const consentSearchSchema = z.object({
  consent_challenge: z.string().optional(),
});

/**
 * Route is /oauth2/consent. After Ory's managed login redirects the browser here with
 * a consent challenge, it asks the user which publishable key the requesting connector
 * (Claude, ChatGPT) should use, then authorizes — handing the chosen key id to the
 * accept call and following the provider redirect to complete the OAuth flow. It
 * renders inside the _auth shell (the split-screen auth layout) via its Outlet.
 */
export const Route = createFileRoute("/_auth/oauth2/consent")({
  component: ConsentRoute,
  validateSearch: consentSearchSchema,
});

/**
 * ConsentFailure is how a failed consent call reads to the person on this screen:
 *
 * - "session-expired" — a 401: the Ory session lapsed between the login and this call. It is the
 *   only answer that means it, so it is the only one that asks the person to sign in again.
 * - "refused" — a 400 or a 403 the API answered with its own sentence, localized before it reached
 *   the wire: a challenge that is unknown, expired or already used (400, "start the authorization
 *   again"), a challenge minted for another account (403), or, on the accept, a key that can no
 *   longer be bound (403, revoked or out of the person's reach since the screen loaded). Each
 *   sentence says what to do, and asking the person to sign in again would only bring the same
 *   refusal back.
 * - "failed" — anything else (a 5xx, a dropped connection): the screen's own copy, with a retry
 *   where one can succeed.
 *
 * The refusal's key never reaches the wire, so the status and the sentence are what classify it.
 * The status is read through the SDK's `isMountayaApiError`, which reads the name the error's class
 * declares and never its constructor, so a 401 thrown by a second copy of `@mountaya/client` in
 * the bundle is still a lapsed session.
 */
type ConsentFailure =
  | { readonly kind: "failed" }
  | { readonly kind: "refused"; readonly message: string; readonly status: number }
  | { readonly kind: "session-expired" };

/**
 * consentFailureOf classifies a thrown value as a {@link ConsentFailure}.
 */
const consentFailureOf = (error: unknown): ConsentFailure => {
  if (!isMountayaApiError(error)) {
    return { kind: "failed" };
  }

  if (error.status === 401) {
    return { kind: "session-expired" };
  }

  const message = error.status === 400 || error.status === 403 ? restRefusalMessage(error) : null;

  return message === null ? { kind: "failed" } : { kind: "refused", message, status: error.status };
};

/**
 * ConsentSessionExpired is what the screen shows once the session lapsed, on the load or on a
 * decision: the sentence, and the way back into the login flow. Signing in again does not resume
 * this request (its challenge belongs to the session that lapsed), so the sentence asks the person
 * to start the connection again from their assistant once signed in.
 */
const ConsentSessionExpired = () => {
  const { t } = useTranslation("iam");

  return (
    <Stack gap="lg">
      <AuthPageHeader
        subtitle={t("oauth2.consent.sessionExpired")}
        title={t("oauth2.consent.title")}
      />
      <Button component={Link} to="/login">
        {t("oauth2.consent.signIn")}
      </Button>
    </Stack>
  );
};

/**
 * ConsentRoute drives the consent screen: it reads the challenge off the URL, loads
 * the consent payload and the user's organizations, and routes the decision through
 * the accept mutation to the provider redirect. It owns the data/mutation lifecycle
 * and the redirect; the presentational ConsentScreen owns the two-step organization +
 * key selection.
 *
 * The screen is always shown, and the person always picks the key: nothing is accepted
 * without a click. The payload's `skip` is copied from the provider and is set only for a
 * client an operator marked to skip consent, never by a grant this flow remembered (the
 * accept asks the provider to remember nothing), so honouring it would bind a key and an
 * organization the person never chose.
 *
 * A failure is read by {@link consentFailureOf}: only a 401 asks the person to sign in again,
 * a 400 or 403 shows the API's own sentence (with no retry on the load, which would bring the
 * same refusal back, and a refreshed key list on a refused accept), and anything else the
 * screen's own copy. An accept that fails for a key the inline create just made refreshes the
 * key list whatever the failure, so the retry binds that key and never makes another.
 */
function ConsentRoute() {
  const { t } = useTranslation("iam");
  const { consent_challenge: challenge } = Route.useSearch();

  const consent = useOAuthConsent(restConfig, challenge ?? "");
  const userId = useIdentityStore((state) => state.userId);
  const organizations = useUserOrganizations(restConfig, userId);
  const accept = useAcceptOAuthConsent(restConfig);
  const reject = useRejectOAuthConsent(restConfig);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Set once a decision answered 401: the screen gives way to the sign-in-again state, since no
  // decision this session makes can land any more.
  const [sessionLapsed, setSessionLapsed] = useState(false);

  // accept.mutate is a stable reference from the mutation hook; reading it through a ref
  // lets runAccept depend only on the challenge and the translator, without listing the
  // whole mutation object and refiring on its state churn.
  const acceptMutate = accept.mutate;
  const acceptRef = useRef(acceptMutate);
  acceptRef.current = acceptMutate;

  // reject.mutate is likewise stable; held in a ref so runDeny depends only on the
  // challenge and the translator, not on the mutation object's in-flight churn.
  const rejectMutate = reject.mutate;
  const rejectRef = useRef(rejectMutate);
  rejectRef.current = rejectMutate;

  // consent.refetch is held the same way, so a refused accept and the inline create can re-read
  // the eligible keys without depending on the query object.
  const refetchConsent = consent.refetch;
  const refetchRef = useRef(refetchConsent);
  refetchRef.current = refetchConsent;

  // The inline create made a key the eligible keys do not list yet (its answer was lost, or it is
  // still being made): read them again so the picker shows it.
  const refreshKeys = useCallback(() => {
    void refetchRef.current();
  }, []);

  const eligibleKeys = consent.data?.eligible_keys;

  const runAccept = useCallback(
    (publishableKeyId: string) => {
      if (!challenge) {
        return;
      }

      // A key the eligible keys the screen was drawn from do not list is one the inline create
      // just made: the picker offers no other.
      const madeInline = !(
        eligibleKeys?.some((key) => key.publishable_key_id === publishableKeyId) ?? false
      );

      setErrorMessage(null);
      acceptRef.current(
        { challenge, publishable_key_id: publishableKeyId },
        {
          onError: (error) => {
            const failure = consentFailureOf(error);
            if (failure.kind === "session-expired") {
              setSessionLapsed(true);

              return;
            }

            setErrorMessage(
              failure.kind === "refused" ? failure.message : t("oauth2.consent.acceptError"),
            );
            // The eligible keys are read again after a key refused for one that can no longer be
            // bound (403: revoked, or out of reach since the screen loaded), so the picker drops
            // it, and after any failure for a key the inline create just made, so the picker lists
            // it, chosen, and the retry is Authorize for that key, never a second create.
            if ((failure.kind === "refused" && failure.status === 403) || madeInline) {
              void refetchRef.current();
            }
          },
          // The redirect goes back to Ory to complete the OAuth flow, so it is a
          // full-page navigation out of the SPA, not a router navigation.
          onSuccess: (redirect) => window.location.assign(redirect.redirect_to),
        },
      );
    },
    [challenge, eligibleKeys, t],
  );

  // Denying rejects the consent through the provider so the requesting connector
  // receives a clean access_denied, then follows the returned redirect — never a bare
  // navigation to "/", which would strand the connector without a decision. A 401
  // surfaces the sign-in-again state, a refusal the API's sentence, and any other failure
  // the screen's own copy, mirroring the accept path.
  const runDeny = useCallback(() => {
    if (!challenge) {
      return;
    }

    setErrorMessage(null);
    rejectRef.current(challenge, {
      onError: (error) => {
        const failure = consentFailureOf(error);
        if (failure.kind === "session-expired") {
          setSessionLapsed(true);

          return;
        }

        setErrorMessage(
          failure.kind === "refused" ? failure.message : t("oauth2.consent.denyError"),
        );
      },
      // Like the accept redirect, this leaves the SPA for the provider, so it is a
      // full-page navigation rather than a router navigation.
      onSuccess: (redirect) => window.location.assign(redirect.redirect_to),
    });
  }, [challenge, t]);

  if (!challenge) {
    return (
      <Stack gap="lg">
        <AuthPageHeader
          subtitle={t("oauth2.consent.missingChallenge")}
          title={t("oauth2.consent.title")}
        />
      </Stack>
    );
  }

  if (sessionLapsed) {
    return <ConsentSessionExpired />;
  }

  if (consent.isError) {
    const failure = consentFailureOf(consent.error);
    if (failure.kind === "session-expired") {
      return <ConsentSessionExpired />;
    }

    // A refused load cannot succeed by asking again: a challenge that is no longer valid is
    // refused the same way every time, and so is one minted for another account, which only
    // signing in as that account can open.
    if (failure.kind === "refused") {
      return (
        <Stack gap="lg">
          <AuthPageHeader subtitle={failure.message} title={t("oauth2.consent.title")} />
          {failure.status === 403 ? (
            <>
              <Text size="sm">{t("oauth2.consent.otherAccount")}</Text>
              <Button component={Link} to="/logout" variant="default">
                {t("oauth2.consent.signOut")}
              </Button>
            </>
          ) : null}
        </Stack>
      );
    }

    return (
      <Stack gap="lg">
        <AuthPageHeader
          subtitle={t("oauth2.consent.loadError")}
          title={t("oauth2.consent.title")}
        />
        <Button onClick={() => void consent.refetch()} variant="default">
          {t("oauth2.consent.retry")}
        </Button>
      </Stack>
    );
  }

  // Wait for the consent payload and the organization list together: step 1 lists the
  // user's active-member orgs, so rendering before that list settles would flash an
  // empty picker. A failed org read resolves isPending to false, so it falls through to
  // the screen (which shows the subscription-required empty state) rather than hanging.
  if (!consent.data || organizations.isPending) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  return (
    <>
      <ConsentScreen
        consent={consent.data}
        denying={reject.isPending}
        errorMessage={errorMessage}
        onAuthorize={runAccept}
        onDeny={runDeny}
        onKeysStale={refreshKeys}
        organizations={organizations.data ?? []}
        pending={accept.isPending}
      />
      {/* The inline payment dialog is mounted on this route too: the consent screen is not
          under the authenticated _app shell that normally mounts it, so the offer to add the
          card a self-serve organization's Data API needs would have nowhere to render. It is
          dormant until that offer is taken. */}
      <PaymentMethodModal />
    </>
  );
}
